import React from 'react';
import { Grid } from '@material-ui/core';
import {
  Margin,
  WhiteSection,
  Flex,
  Padding
} from 'components/base/layout-components';
import CycleTime from './components/CycleTime';
import WorkBreakdown from './components/WorkBreakdown';
import ErrorBoundary from 'components/ErrorBoundary';
import ActivityOverviewContainer from 'components/ActivityOverviewContainer';
import MetricsOverviewContainer from 'components/MetricsOverviewContainer';
import NoDataWhiteSection from 'components/NoDataWhiteSection';
import ContributorsTeamsMetrics from 'components/ContributorsTeamsMetrics';
import { getWorkBreakdownConfigurations } from './util';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import {
  getCycleTimeConfigurations,
  goToBranchesOrderBy
} from 'views/root/views/home/views/dashboard/dashboard.service';
import { useHistory } from 'react-router';
import { StyledWidgetHeader } from 'components/base/common-styles';
import { metricNames } from '@linearb/linjs-common';

const AllTeamsDashboard = props => {
  const {
    selectedTeam,
    activities,
    metrics,
    cycleTimeGridSize,
    workBreakdownTrendData,
    cycleTimeTrendData,
    cycleTimeAverage,
    totalCycleTimeInMinutes,
    pipelineMilestones,
    orgWorkBreakdownData,
    isFinishedOnBoarding,
    contributors,
    isRetrospect,
    toggleModal,
    uiState,
    currentIteration,
    currentTeams,
    toggleSnackBar,
    getTeams,
    createTeam,
    search,
    showLoader,
    isScoresReady,
    shouldHidePersonalInformationDashboard
  } = props;
  const history = useHistory();
  const { dates, selectedTeamId } = uiState;
  return (
    <>
      <Grid item xs={12 - cycleTimeGridSize}>
        <Margin r='small' b='small'>
          <WhiteSection borderRadius='0.45rem' height='22rem'>
            <ErrorBoundary componentName='Work Breakdown'>
              <WorkBreakdown
                trendData={workBreakdownTrendData}
                onLinkIconClick={() => {
                  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                    page: TRACKING.PAGES.DASHBOARD,
                    click_type: TRACKING.CLICK.WORK_BREAKDOWN_CONTEXT
                  });
                  goToBranchesOrderBy(
                    'code_changes,desc',
                    uiState,
                    selectedTeam,
                    history
                  );
                }}
                configurations={getWorkBreakdownConfigurations(
                  orgWorkBreakdownData
                )}
                headLine={{
                  value: orgWorkBreakdownData.total,
                  description: 'Code Changes'
                }}
              />
            </ErrorBoundary>
          </WhiteSection>
        </Margin>
      </Grid>
      <Grid item xs={cycleTimeGridSize}>
        <WhiteSection display='flex' borderRadius='0.45rem' height='22rem'>
          <ErrorBoundary componentName='Cycle Time'>
            <CycleTime
              toggleModal={toggleModal}
              value={totalCycleTimeInMinutes}
              trendData={cycleTimeTrendData}
              selectedTeamId={selectedTeamId}
              onLinkIconClick={() => {
                TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                  page: TRACKING.PAGES.DASHBOARD,
                  click_type: TRACKING.CLICK.CYCLE_TIME_CONTEXT
                });
                goToBranchesOrderBy(
                  'cycle_time,desc',
                  uiState,
                  selectedTeam,
                  history,
                  metricNames.CYCLE_TIME,
                  'cycle_time_updated'
                );
              }}
              configurations={getCycleTimeConfigurations(pipelineMilestones)}
              avgTime={cycleTimeAverage}
              dates={dates}
            />
          </ErrorBoundary>
        </WhiteSection>
      </Grid>
      <Grid item xs={8}>
        <Flex direction='column' height='100%'>
          <Flex direction='column' grow='1'>
            <Flex direction='row' height='50%'>
              <Padding b='small' width='100%'>
                <WhiteSection
                  l='1rem'
                  r='1rem'
                  t='1rem'
                  b='1rem'
                  height='100%'
                  width='100%'
                >
                  <ErrorBoundary componentName='Iteration Overview'>
                    <ActivityOverviewContainer
                      activities={activities}
                      history={history}
                      isFinishedOnBoarding={isFinishedOnBoarding}
                      dataCyPrefix='dashboard-iteration-overview'
                    />
                  </ErrorBoundary>
                </WhiteSection>
              </Padding>
            </Flex>

            <Flex direction='row' height='50%'>
              <Padding width='100%'>
                <WhiteSection height='100%' width='100%'>
                  <ErrorBoundary componentName='Delivery Pipeline'>
                    <MetricsOverviewContainer
                      metrics={metrics}
                      isScoresReady={isScoresReady}
                      pipelineMilestones={pipelineMilestones}
                      dataCyPrefix='dashboard-delivery-pipeline'
                    />
                  </ErrorBoundary>
                </WhiteSection>
              </Padding>
            </Flex>
          </Flex>
        </Flex>
      </Grid>
      <Grid item xs={4}>
        <Flex ml='small' mb='small' direction='column' height='100%' grow='0'>
          <Flex height='100%'>
            <NoDataWhiteSection
              svg='People'
              text='No contributors activity is recorded for this iteration yet'
              noDataConditionFc={() =>
                !currentTeams.length && !contributors.length
              }
            >
              <ErrorBoundary componentName='Contributors'>
                <Padding
                  l='medium'
                  t='medium'
                  r='medium'
                  b='medium'
                  width='100%'
                  height='100%'
                  box='border-box'
                  data-cy='all-teams-contributors-table'
                >
                  <Flex direction='column' height='100%'>
                    <StyledWidgetHeader style={{ paddingBottom: '2rem' }}>
                      Contributors
                    </StyledWidgetHeader>

                    <Flex
                      grow={1}
                      direction='column'
                      height='100%'
                      width='100%'
                    >
                      <ContributorsTeamsMetrics
                        toggleModal={toggleModal}
                        toggleSnackBar={toggleSnackBar}
                        createTeam={createTeam}
                        history={history}
                        search={search}
                        isRetrospect={isRetrospect}
                        dates={dates}
                        teams={currentTeams}
                        contributors={contributors}
                        currentIteration={currentIteration}
                        getTeams={getTeams}
                        showLoader={showLoader}
                        shouldHidePersonalInformationDashboard={
                          shouldHidePersonalInformationDashboard
                        }
                      />
                    </Flex>
                  </Flex>
                </Padding>
              </ErrorBoundary>
            </NoDataWhiteSection>
          </Flex>
        </Flex>
      </Grid>
    </>
  );
};

export default AllTeamsDashboard;
